import { User2Icon, UserPlusIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import useDebounce from "react-use/lib/useDebounce";

import { getDonorIds } from "@blis/shared/models/subject";
import { trpc } from "@blis/trpc/react";

import { SearchableSelect } from "@/components/ui/searchable-select";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { TSubjectDetail } from "@/lib/types";
import { format } from "@/lib/utils";

type SubjectSelectProps = {
  value?: TSubjectDetail;
  onValueChanged: (subject: TSubjectDetail) => void;
  onCreateSelected?: (value: {
    value: string;
    type: "caseId" | "unosId";
  }) => void;
  buttonClassName?: string;
  placeholder?: string;
  searchPlaceholder?: string;
};

export function SubjectSelect({
  value,
  onValueChanged,
  onCreateSelected,
  buttonClassName,
  placeholder = "Select...",
  searchPlaceholder = "Search Donor IDs...",
}: SubjectSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");

  useDebounce(
    () => {
      setSearch(input);
    },
    250,
    [input],
  );

  useEffect(() => {
    if (!open) {
      setSearch("");
      setInput("");
    }
  }, [open]);

  const { data } = trpc.subjects.search.useQuery({
    query: search,
    take: 10,
  });

  const subjects = data || [];

  const containsSelected = subjects.some((s) => s.id === value?.id);

  const subjectsWithSelected =
    value && !containsSelected ? [value, ...subjects] : subjects;

  const internalOnCreateSelected = useCallback(
    (value: { value: string; type: "caseId" | "unosId" }) => {
      setOpen(false);
      setSearch("");
      setInput("");
      if (onCreateSelected) {
        onCreateSelected(value);
      }
    },
    [onCreateSelected],
  );

  const creatableValue = input.trim();
  const createKey = "__create__";

  return (
    <SearchableSelect
      filter={() => true}
      isOpen={open}
      onOpenChange={setOpen}
      selectionMode="single"
      selectedKeys={value?.id ? new Set([value.id]) : new Set()}
      onSelectionChange={(keys) => {
        setSearch("");
        if (keys === "all") return;
        const firstKey = keys.values().next().value;
        if (!firstKey) return;

        if (firstKey === createKey) {
          internalOnCreateSelected({
            value: creatableValue,
            type: guessIdType(creatableValue),
          });
          return;
        }

        const subject = subjectsWithSelected.find((s) => s.id === firstKey);
        if (subject) {
          onValueChanged(subject);
        }
      }}
      renderEmptyState={() => (
        <Text muted size="1" className="p-2">
          No donors found
        </Text>
      )}
    >
      <SearchableSelect.Button
        id="subject-select"
        type="button"
        className={cn("justify-between", buttonClassName)}
      >
        <SearchableSelect.Value
          items={subjectsWithSelected}
          getKey={(item) => item.id}
          getText={(item) => item.caseId}
          placeholder={placeholder}
          className="flex-1 text-start"
        >
          {({ selectedItems }) => (
            <span className="min-w-0 flex items-center">
              <User2Icon className="mr-2 size-4 shrink-0" />
              <span className="truncate">{selectedItems[0]?.caseId}</span>
            </span>
          )}
        </SearchableSelect.Value>
      </SearchableSelect.Button>
      <SearchableSelect.Popover
        placement="bottom end"
        className="w-full sm:max-w-[320px]"
      >
        <SearchableSelect.Input
          placeholder={searchPlaceholder}
          value={input}
          onChange={(event) => {
            setInput(event.currentTarget.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;

            if (input.length > 0) {
              event.preventDefault();
              event.stopPropagation();
              setInput("");
              setSearch("");
              return;
            }

            setOpen(false);
          }}
        />

        <SearchableSelect.List shouldFocusOnHover>
          {subjectsWithSelected.map((subject) => {
            return (
              <SearchableSelect.Item
                key={subject.id}
                id={subject.id}
                textValue={getSubjectTextValue(subject)}
              >
                <SubjectSelectItem subject={subject} />
              </SearchableSelect.Item>
            );
          })}
          {onCreateSelected && creatableValue && (
            <SearchableSelect.Item
              key={createKey}
              id={createKey}
              textValue={creatableValue}
            >
              <div className="flex items-center gap-2">
                <UserPlusIcon className="size-4 shrink-0" />

                <Text className="shrink-0">
                  Create with{" "}
                  {guessIdType(creatableValue) === "caseId"
                    ? "Donor ID"
                    : "UNOS ID"}
                  :
                </Text>
                <Text bold truncate>
                  {creatableValue}
                </Text>
              </div>
            </SearchableSelect.Item>
          )}
        </SearchableSelect.List>
      </SearchableSelect.Popover>
    </SearchableSelect>
  );
}

// unos ID is 4 characters followed by 3 digits
const unosIdRegex = /^[A-Za-z]{4}\d{3}$/;

function guessIdType(search: string): "unosId" | "caseId" {
  if (unosIdRegex.test(search)) {
    return "unosId";
  }
  return "caseId";
}

function SubjectSelectItem({ subject }: { subject: TSubjectDetail }) {
  const ids = getDonorIds(subject);
  const details = [
    ids.unosId ? `UNOS ${ids.unosId}` : null,
    ids.donorIds.length > 0 ? `Alt ${ids.donorIds.join(", ")}` : null,
    subject.dob ? `DOB ${format(subject.dob, { time: false })}` : null,
    subject.sex ? `Sex ${subject.sex}` : null,
  ].filter((detail) => !!detail);

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Text elementType="div" bold truncate>
        {subject.caseId}
      </Text>
      {details.length > 0 && (
        <Text elementType="div" size="xs" muted truncate>
          {details.join(" | ")}
        </Text>
      )}
    </div>
  );
}

function getSubjectTextValue(subject: TSubjectDetail) {
  const ids = getDonorIds(subject);
  return [
    subject.caseId,
    ids.unosId,
    ...ids.donorIds,
    subject.dob ? format(subject.dob, { time: false }) : null,
    subject.sex,
  ]
    .filter((value) => !!value)
    .join(" ");
}
